import type { StudentT } from 'types/shared/core/Student';
import { handleDates } from 'utils/api/dateHandler';

const DB_NAME = 'anotherclass-student-list-cache';
const DB_VERSION = 2;
const CACHE_SCHEMA_VERSION = 1;
const SCOPE_STORE_NAME = 'scopes';
const LOCK_STORE_NAME = 'locks';
const LOCK_POLL_INTERVAL_MS = 100;
const LOCK_DURATION_MS = 5_000;
const STUDENT_STORE_NAME = 'students';
const STUDENT_SCOPE_INDEX_NAME = 'byScope';
const AES_KEY_BYTE_LENGTH = 32;
const AES_GCM_IV_BYTE_LENGTH = 12;
const CRYPTO_BATCH_SIZE = 100;

export type StudentListCacheScopeT = {
  academyId: number;
  teacherId: number;
};

export type StudentListCacheScopeLockT = StudentListCacheScopeT & {
  ownerId: string;
  expiresAt: number;
};

export type StudentListCacheScopeMetadataT = StudentListCacheScopeT & {
  lastSyncedAt: string;
  encDEK: string;
  lastFullSyncedAt: string;
  schemaVersion: number;
};

export type DeletedStudentTombstoneT = Pick<StudentT, 'id' | 'name'> & {
  status: 'deleted';
};

export type CachedStudentT = StudentT | DeletedStudentTombstoneT;

export type StudentListCacheDiagnosticsT = {
  studentRowCount: number;
  encDEKIv: string | null;
  studentRowIvs: Array<{ studentId: number; iv: string }>;
  hasDuplicatedStudentRowIv: boolean;
};

type StoredStudentPayloadT = StudentT | Pick<StudentT, 'id' | 'name'>;

type StudentCacheRowT = StudentListCacheScopeT & {
  studentId: number;
  encJson: string;
  updatedAt: string;
};

type EncryptedBlobT = {
  alg: 'A256GCM';
  iv: string;
  ct: string;
};

type UnlockedScopeT = {
  cacheKey: string;
  dek: CryptoKey;
  encDEK: string;
};

type UnlockScopeResultT = {
  metadata: StudentListCacheScopeMetadataT;
  usedPreviousKek: boolean;
};

// IndexedDB request의 callback 결과를 await 가능한 Promise로 변환한다.
const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });

// IndexedDB transaction 전체의 완료 또는 실패를 await한다.
const transactionToPromise = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(transaction.error || new Error('IndexedDB transaction aborted'));
    transaction.onerror = () =>
      reject(transaction.error || new Error('IndexedDB transaction failed'));
  });

// 수천 건의 Web Crypto 작업이 한꺼번에 예약되지 않도록 일정 단위로 나눠 실행한다.
const mapInBatches = async <T, R>(
  items: readonly T[],
  mapper: (item: T) => Promise<R>,
): Promise<R[]> => {
  const result: R[] = [];

  for (let index = 0; index < items.length; index += CRYPTO_BATCH_SIZE) {
    const batch = items.slice(index, index + CRYPTO_BATCH_SIZE);
    result.push(...(await Promise.all(batch.map(mapper))));
  }

  return result;
};

export default class StudentListCacheDB {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private unlockedScope: UnlockedScopeT | null = null;

  // 현재 브라우저가 이 캐시에 필요한 IndexedDB와 Web Crypto API를 지원하는지 확인한다.
  static isSupported = (): boolean =>
    typeof indexedDB !== 'undefined' &&
    typeof crypto !== 'undefined' &&
    !!crypto.subtle &&
    typeof TextEncoder !== 'undefined' &&
    typeof TextDecoder !== 'undefined';

  // 같은 academy/teacher의 조회·캐시 작업만 직렬화한다. 대기 중에만 100ms 간격으로 재시도한다.
  withScopeLock = async <T>(
    scope: StudentListCacheScopeT,
    operation: (scopeLock: StudentListCacheScopeLockT) => Promise<T>,
  ): Promise<T> => {
    this.validateScope(scope);
    const ownerId = this.encodeBase64(crypto.getRandomValues(new Uint8Array(16)));
    let scopeLock: StudentListCacheScopeLockT | null = null;

    while (!scopeLock) {
      scopeLock = await this.tryAcquireScopeLock(scope, ownerId);
      if (!scopeLock) await new Promise((resolve) => setTimeout(resolve, LOCK_POLL_INTERVAL_MS));
    }

    try {
      const result = await operation(scopeLock);
      await this.assertScopeLock(scope, scopeLock);
      return result;
    } finally {
      await this.releaseScopeLock(scopeLock).catch(() => undefined);
    }
  };

  // 메모리에만 보관하는 평문 DEK 참조를 제거한다.
  lock = (): void => {
    this.unlockedScope = null;
  };

  // DB 연결과 메모리 DEK를 함께 정리한다.
  close = (): void => {
    this.lock();

    if (this.dbPromise) {
      this.dbPromise.then((db) => db.close()).catch(() => undefined);
      this.dbPromise = null;
    }
  };

  // scope metadata를 읽고 스키마나 필드가 잘못됐으면 해당 scope를 폐기한다.
  readScopeMetadata = async (
    scope: StudentListCacheScopeT,
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<StudentListCacheScopeMetadataT | null> => {
    if (!scopeLock) return this.withScopeLock(scope, (lock) => this.readScopeMetadata(scope, lock));
    this.validateScope(scope);
    const metadata = await this.readScopeMetadataRaw(scope);

    if (!metadata) return null;
    // [엣지 상황] 앱 업데이트나 오래된 탭 때문에 서로 다른 cache schema를 읽을 수 있다.
    // 코드 변조로 간주해 중단하지 않고, 폐기 가능한 로컬 복사본만 지워 새 형식으로 다시 받는다.
    if (!this.isValidMetadata(metadata, scope)) {
      await this.clearScope(scope, scopeLock);
      return null;
    }

    return metadata;
  };

  // current KEK, PKEK 순서로 DEK를 복원하고 PKEK 사용 시 current KEK로 다시 감싼다.
  unlockScope = async (
    scope: StudentListCacheScopeT,
    kekBase64: string,
    pkekBase64: string | null,
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<UnlockScopeResultT | null> => {
    if (!scopeLock) {
      return this.withScopeLock(scope, (lock) =>
        this.unlockScope(scope, kekBase64, pkekBase64, lock),
      );
    }
    const metadata = await this.readScopeMetadata(scope, scopeLock);
    if (!metadata) return null;

    const currentKek = await this.importKek(kekBase64);
    const additionalData = this.buildDekAdditionalData(scope);

    try {
      const dek = await this.decryptAndImportDek(metadata.encDEK, currentKek, additionalData);
      this.unlockedScope = {
        cacheKey: this.buildScopeCacheKey(scope),
        dek,
        encDEK: metadata.encDEK,
      };
      return { metadata, usedPreviousKek: false };
    } catch {
      // 월이 바뀐 직후에는 직전 월 KEK로 한 번 더 복구한다.
    }

    if (pkekBase64) {
      try {
        const previousKek = await this.importKek(pkekBase64);
        const rawDek = await this.decryptBytes(metadata.encDEK, previousKek, additionalData);

        try {
          if (rawDek.byteLength !== AES_KEY_BYTE_LENGTH) {
            throw new Error('Invalid DEK length');
          }

          const [dek, encDEK] = await Promise.all([
            this.importAesKey(rawDek),
            this.encryptBytes(rawDek, currentKek, additionalData),
          ]);
          const updatedMetadata = { ...metadata, encDEK };
          await this.writeScopeMetadata(updatedMetadata, scopeLock);
          this.unlockedScope = { cacheKey: this.buildScopeCacheKey(scope), dek, encDEK };
          return { metadata: updatedMetadata, usedPreviousKek: true };
        } finally {
          rawDek.fill(0);
        }
      } catch {
        // current/previous KEK 모두 실패하면 아래에서 scope 전체를 폐기한다.
      }
    }

    // [엣지 상황] 전월 KEK로도 열 수 없을 만큼 장기 미접속하면 current KEK와 PKEK가 모두 실패한다.
    // [비정상 상황] 서버 KEK 유실이나 다른 scope 혼입도 같으며, scope 폐기 후 full sync로 복구한다.
    await this.clearScope(scope, scopeLock);
    return null;
  };

  // full sync 결과를 새 DEK로 암호화해 기존 scope 전체와 원자적으로 교체한다.
  replaceScope = async (
    scope: StudentListCacheScopeT,
    kekBase64: string,
    students: readonly StudentT[],
    syncedAt: string | Date,
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<void> => {
    if (!scopeLock) {
      return this.withScopeLock(scope, (lock) =>
        this.replaceScope(scope, kekBase64, students, syncedAt, lock),
      );
    }
    this.validateScope(scope);
    const normalizedSyncedAt = this.normalizeTimestamp(syncedAt, 'syncedAt');

    const kek = await this.importKek(kekBase64);
    // [흔한 정책] 모든 full sync는 새 256-bit DEK를 만들고 학생 row마다 새 IV를 만든다.
    // 따라서 같은 payload를 다시 저장해도 이전 DEK와 학생별 암호문/IV를 재사용하지 않는다.
    const rawDek = crypto.getRandomValues(new Uint8Array(AES_KEY_BYTE_LENGTH));

    try {
      const [dek, encDEK] = await Promise.all([
        this.importAesKey(rawDek),
        this.encryptBytes(rawDek, kek, this.buildDekAdditionalData(scope)),
      ]);
      const metadata: StudentListCacheScopeMetadataT = {
        ...scope,
        lastSyncedAt: normalizedSyncedAt,
        encDEK,
        lastFullSyncedAt: normalizedSyncedAt,
        schemaVersion: CACHE_SCHEMA_VERSION,
      };
      const rows = await this.encryptStudentRows(scope, dek, students, normalizedSyncedAt);

      await this.replaceScopeRows(scope, metadata, rows, scopeLock);
      this.unlockedScope = {
        cacheKey: this.buildScopeCacheKey(scope),
        dek,
        encDEK: metadata.encDEK,
      };
    } finally {
      rawDek.fill(0);
    }
  };

  // 증분 payload를 반영하고 조회 범위에서 빠진 기존 학생은 tombstone으로 축소한다.
  applyIncrementalSync = async (
    scope: StudentListCacheScopeT,
    students: readonly StudentT[],
    authoritativeStudentIds: readonly number[],
    syncedAt: string | Date,
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<boolean> => {
    if (!scopeLock) {
      return this.withScopeLock(scope, (lock) =>
        this.applyIncrementalSync(scope, students, authoritativeStudentIds, syncedAt, lock),
      );
    }
    const normalizedSyncedAt = this.normalizeTimestamp(syncedAt, 'syncedAt');
    const dek = this.getUnlockedDek(scope);
    const metadata = await this.readScopeMetadata(scope, scopeLock);
    if (!metadata) return false;

    const cachedStudents = await this.readAllCachedStudents(scope, scopeLock);
    if (!cachedStudents) return false;

    const authoritativeStudentIdSet = new Set(authoritativeStudentIds);
    const updatedStudentIdSet = new Set(students.map((student) => student.id));
    const studentsToWrite: StoredStudentPayloadT[] = [...students];

    // 조회 범위에서 빠진 학생은 이름만 남겨 삭제생과 같은 tombstone으로 취급한다.
    cachedStudents.forEach((student) => {
      if (
        !authoritativeStudentIdSet.has(student.id) &&
        !updatedStudentIdSet.has(student.id) &&
        student.status !== 'deleted'
      ) {
        studentsToWrite.push({ id: student.id, name: student.name });
      }
    });

    const rows = await this.encryptStudentRows(scope, dek, studentsToWrite, normalizedSyncedAt);
    const updatedMetadata = { ...metadata, lastSyncedAt: normalizedSyncedAt };
    const { transaction, completion } = await this.createScopeWriteTransaction(scope, scopeLock, [
      SCOPE_STORE_NAME,
      STUDENT_STORE_NAME,
    ]);
    const scopeStore = transaction.objectStore(SCOPE_STORE_NAME);
    const studentStore = transaction.objectStore(STUDENT_STORE_NAME);

    scopeStore.put(updatedMetadata);
    rows.forEach((row) => studentStore.put(row));
    await completion;
    return true;
  };

  // 삭제생의 id와 이름만 암호화해 tombstone으로 저장한다.
  putDeletedStudents = async (
    scope: StudentListCacheScopeT,
    students: ReadonlyArray<Pick<StudentT, 'id' | 'name'>>,
    updatedAt: string | Date = new Date(),
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<boolean> => {
    if (!scopeLock) {
      return this.withScopeLock(scope, (lock) =>
        this.putDeletedStudents(scope, students, updatedAt, lock),
      );
    }
    const normalizedUpdatedAt = this.normalizeTimestamp(updatedAt, 'updatedAt');
    const metadata = await this.readScopeMetadata(scope, scopeLock);
    if (!metadata || this.unlockedScope?.encDEK !== metadata.encDEK) return false;
    const dek = this.getUnlockedDek(scope);

    const tombstones = students.map(({ id, name }) => ({ id, name }));
    const rows = await this.encryptStudentRows(scope, dek, tombstones, normalizedUpdatedAt);
    const { transaction, completion } = await this.createScopeWriteTransaction(scope, scopeLock, [
      STUDENT_STORE_NAME,
    ]);
    const store = transaction.objectStore(STUDENT_STORE_NAME);

    rows.forEach((row) => store.put(row));
    await completion;
    return true;
  };

  // 복호화된 캐시 중 현재 학생 full payload만 반환한다.
  readStudents = async (
    scope: StudentListCacheScopeT,
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<StudentT[] | null> => {
    if (!scopeLock) return this.withScopeLock(scope, (lock) => this.readStudents(scope, lock));
    const cachedStudents = await this.readAllCachedStudents(scope, scopeLock);
    if (!cachedStudents) return null;

    return cachedStudents.filter((student): student is StudentT => student.status !== 'deleted');
  };

  // 저장된 삭제생 tombstone을 필요 ID 범위로 조회한다.
  readDeletedStudents = async (
    scope: StudentListCacheScopeT,
    studentIds?: readonly number[],
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<DeletedStudentTombstoneT[] | null> => {
    if (!scopeLock) {
      return this.withScopeLock(scope, (lock) => this.readDeletedStudents(scope, studentIds, lock));
    }
    const cachedStudents = await this.readAllCachedStudents(scope, scopeLock);
    if (!cachedStudents) return null;

    const studentIdSet = studentIds ? new Set(studentIds) : null;
    return cachedStudents.filter(
      (student): student is DeletedStudentTombstoneT =>
        student.status === 'deleted' && (!studentIdSet || studentIdSet.has(student.id)),
    );
  };

  // authoritative ID 중 full payload가 없거나 tombstone만 있는 ID를 찾는다.
  findMissingStudentIds = async (
    scope: StudentListCacheScopeT,
    studentIds: readonly number[],
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<number[] | null> => {
    if (!scopeLock) {
      return this.withScopeLock(scope, (lock) =>
        this.findMissingStudentIds(scope, studentIds, lock),
      );
    }
    const students = await this.readStudents(scope, scopeLock);
    if (!students) return null;

    const cachedStudentIdSet = new Set(students.map((student) => student.id));
    return [...new Set(studentIds)].filter((studentId) => !cachedStudentIdSet.has(studentId));
  };

  // 암호문을 노출하지 않고 scope의 row 수와 IV 중복 여부를 진단할 정보를 반환한다.
  readScopeDiagnostics = async (
    scope: StudentListCacheScopeT,
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<StudentListCacheDiagnosticsT> => {
    if (!scopeLock)
      return this.withScopeLock(scope, (lock) => this.readScopeDiagnostics(scope, lock));
    const metadata = await this.readScopeMetadata(scope, scopeLock);
    const db = await this.openDB();
    const transaction = db.transaction(STUDENT_STORE_NAME, 'readonly');
    const rows = (await requestToPromise(
      transaction
        .objectStore(STUDENT_STORE_NAME)
        .index(STUDENT_SCOPE_INDEX_NAME)
        .getAll(IDBKeyRange.only(this.buildScopeKey(scope))),
    )) as StudentCacheRowT[];
    const studentRowIvs = rows.map((row) => ({
      studentId: row.studentId,
      iv: this.parseEncryptedBlob(row.encJson).iv,
    }));
    const uniqueIvCount = new Set(studentRowIvs.map(({ iv }) => iv)).size;

    return {
      studentRowCount: rows.length,
      encDEKIv: metadata ? this.parseEncryptedBlob(metadata.encDEK).iv : null,
      studentRowIvs,
      hasDuplicatedStudentRowIv: uniqueIvCount !== studentRowIvs.length,
    };
  };

  // 지정한 academy/teacher scope의 metadata와 학생 row를 모두 삭제한다.
  clearScope = async (
    scope: StudentListCacheScopeT,
    scopeLock?: StudentListCacheScopeLockT,
  ): Promise<void> => {
    if (!scopeLock) return this.withScopeLock(scope, (lock) => this.clearScope(scope, lock));
    this.validateScope(scope);
    if (this.unlockedScope?.cacheKey === this.buildScopeCacheKey(scope)) this.lock();

    const { transaction, completion } = await this.createScopeWriteTransaction(scope, scopeLock, [
      SCOPE_STORE_NAME,
      STUDENT_STORE_NAME,
    ]);
    const scopeStore = transaction.objectStore(SCOPE_STORE_NAME);
    const studentStore = transaction.objectStore(STUDENT_STORE_NAME);

    scopeStore.delete(this.buildScopeKey(scope));
    this.deleteStudentRowsInTransaction(studentStore, scope);
    await completion;
  };

  // 기존 캐시를 유지하며 scope/student 및 scope별 lock object store를 준비한다.
  private openDB = (): Promise<IDBDatabase> => {
    if (!StudentListCacheDB.isSupported()) {
      return Promise.reject(new Error('IndexedDB or Web Crypto is not supported'));
    }
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(LOCK_STORE_NAME)) {
          db.createObjectStore(LOCK_STORE_NAME, { keyPath: ['academyId', 'teacherId'] });
        }
        if (!db.objectStoreNames.contains(SCOPE_STORE_NAME)) {
          db.createObjectStore(SCOPE_STORE_NAME, {
            keyPath: ['academyId', 'teacherId'],
          });
        }
        if (!db.objectStoreNames.contains(STUDENT_STORE_NAME)) {
          const studentStore = db.createObjectStore(STUDENT_STORE_NAME, {
            keyPath: ['academyId', 'teacherId', 'studentId'],
          });
          studentStore.createIndex(STUDENT_SCOPE_INDEX_NAME, ['academyId', 'teacherId']);
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          this.dbPromise = null;
          this.lock();
        };
        resolve(db);
      };
      request.onerror = () => {
        this.dbPromise = null;
        reject(request.error || new Error('Failed to open student list cache'));
      };
      request.onblocked = () => {
        this.dbPromise = null;
        reject(new Error('Student list cache upgrade is blocked'));
      };
    });

    return this.dbPromise;
  };

  // lock 확인과 등록을 한 readwrite transaction에서 처리해 동시 획득을 막는다.
  private tryAcquireScopeLock = async (
    scope: StudentListCacheScopeT,
    ownerId: string,
  ): Promise<StudentListCacheScopeLockT | null> => {
    const db = await this.openDB();
    const transaction = db.transaction(LOCK_STORE_NAME, 'readwrite');
    const completion = transactionToPromise(transaction);
    const store = transaction.objectStore(LOCK_STORE_NAME);
    let acquired: StudentListCacheScopeLockT | null = null;
    const request = store.get(this.buildScopeKey(scope));
    request.onsuccess = () => {
      const current = request.result as StudentListCacheScopeLockT | undefined;
      const now = Date.now();
      if (current && current.expiresAt > now) return;
      acquired = { ...scope, ownerId, expiresAt: now + LOCK_DURATION_MS };
      store.put(acquired);
    };
    await completion;
    return acquired;
  };

  // 만료 후 다른 작업이 인계받은 lock은 이전 작업이 해제하지 못한다.
  private releaseScopeLock = async (scopeLock: StudentListCacheScopeLockT): Promise<void> => {
    const db = await this.openDB();
    const transaction = db.transaction(LOCK_STORE_NAME, 'readwrite');
    const completion = transactionToPromise(transaction);
    const store = transaction.objectStore(LOCK_STORE_NAME);
    const request = store.get(this.buildScopeKey(scopeLock));
    request.onsuccess = () => {
      if (request.result?.ownerId === scopeLock.ownerId)
        store.delete(this.buildScopeKey(scopeLock));
    };
    await completion;
  };

  private isScopeLockOwner = (
    scope: StudentListCacheScopeT,
    scopeLock: StudentListCacheScopeLockT,
    current: StudentListCacheScopeLockT | undefined,
  ): boolean =>
    scopeLock.academyId === scope.academyId &&
    scopeLock.teacherId === scope.teacherId &&
    current?.ownerId === scopeLock.ownerId &&
    current.expiresAt > Date.now();

  // 복호화 중 잠금이 만료됐다면 캐시 결과를 성공으로 반환하지 않는다.
  private assertScopeLock = async (
    scope: StudentListCacheScopeT,
    scopeLock: StudentListCacheScopeLockT,
  ): Promise<void> => {
    const db = await this.openDB();
    const transaction = db.transaction(LOCK_STORE_NAME, 'readonly');
    const current = await requestToPromise(
      transaction.objectStore(LOCK_STORE_NAME).get(this.buildScopeKey(scope)),
    );
    if (!this.isScopeLockOwner(scope, scopeLock, current)) {
      throw new Error('Student list cache lock expired or changed owner');
    }
  };

  // 소유권 확인과 캐시 변경이 분리되지 않도록 lock store를 같은 transaction에 포함한다.
  private createScopeWriteTransaction = async (
    scope: StudentListCacheScopeT,
    scopeLock: StudentListCacheScopeLockT,
    storeNames: string[],
  ): Promise<{ transaction: IDBTransaction; completion: Promise<void> }> => {
    const db = await this.openDB();
    const transaction = db.transaction([LOCK_STORE_NAME, ...storeNames], 'readwrite');
    const completion = transactionToPromise(transaction);
    try {
      const current = await requestToPromise(
        transaction.objectStore(LOCK_STORE_NAME).get(this.buildScopeKey(scope)),
      );
      if (!this.isScopeLockOwner(scope, scopeLock, current)) {
        throw new Error('Student list cache lock expired or changed owner');
      }
      return { transaction, completion };
    } catch (error) {
      transaction.abort();
      await completion.catch(() => undefined);
      throw error;
    }
  };

  // 검증 전 상태의 scope metadata 한 건을 IndexedDB에서 읽는다.
  private readScopeMetadataRaw = async (
    scope: StudentListCacheScopeT,
  ): Promise<StudentListCacheScopeMetadataT | null> => {
    const db = await this.openDB();
    const transaction = db.transaction(SCOPE_STORE_NAME, 'readonly');
    const result = await requestToPromise(
      transaction.objectStore(SCOPE_STORE_NAME).get(this.buildScopeKey(scope)),
    );
    return (result as StudentListCacheScopeMetadataT | undefined) || null;
  };

  // DEK 재래핑 등 metadata만 바뀌는 경우 scope row를 갱신한다.
  private writeScopeMetadata = async (
    metadata: StudentListCacheScopeMetadataT,
    scopeLock: StudentListCacheScopeLockT,
  ): Promise<void> => {
    const { transaction, completion } = await this.createScopeWriteTransaction(
      metadata,
      scopeLock,
      [SCOPE_STORE_NAME],
    );
    transaction.objectStore(SCOPE_STORE_NAME).put(metadata);
    await completion;
  };

  // 기존 scope 학생 row를 모두 지운 뒤 새 metadata와 학생 row를 같은 transaction에 쓴다.
  private replaceScopeRows = async (
    scope: StudentListCacheScopeT,
    metadata: StudentListCacheScopeMetadataT,
    rows: readonly StudentCacheRowT[],
    scopeLock: StudentListCacheScopeLockT,
  ): Promise<void> => {
    const { transaction, completion } = await this.createScopeWriteTransaction(scope, scopeLock, [
      SCOPE_STORE_NAME,
      STUDENT_STORE_NAME,
    ]);
    const studentStore = transaction.objectStore(STUDENT_STORE_NAME);
    const cursorRequest = studentStore
      .index(STUDENT_SCOPE_INDEX_NAME)
      .openKeyCursor(IDBKeyRange.only(this.buildScopeKey(scope)));

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (cursor) {
        studentStore.delete(cursor.primaryKey);
        cursor.continue();
        return;
      }

      rows.forEach((row) => studentStore.put(row));
      transaction.objectStore(SCOPE_STORE_NAME).put(metadata);
    };

    await completion;
  };

  // 이미 열린 transaction 안에서 해당 scope의 모든 학생 row 삭제를 예약한다.
  private deleteStudentRowsInTransaction = (
    studentStore: IDBObjectStore,
    scope: StudentListCacheScopeT,
  ): void => {
    const cursorRequest = studentStore
      .index(STUDENT_SCOPE_INDEX_NAME)
      .openKeyCursor(IDBKeyRange.only(this.buildScopeKey(scope)));

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) return;
      studentStore.delete(cursor.primaryKey);
      cursor.continue();
    };
  };

  // scope의 모든 row를 복호화하고 tombstone에는 runtime deleted 상태를 합성한다.
  private readAllCachedStudents = async (
    scope: StudentListCacheScopeT,
    scopeLock: StudentListCacheScopeLockT,
  ): Promise<CachedStudentT[] | null> => {
    const metadata = await this.readScopeMetadata(scope, scopeLock);
    // 다른 WebView가 교체한 캐시는 이전 DEK로 읽거나 손상으로 판단해 삭제하지 않는다.
    if (!metadata || this.unlockedScope?.encDEK !== metadata.encDEK) return null;
    const dek = this.getUnlockedDek(scope);
    const db = await this.openDB();
    const transaction = db.transaction(STUDENT_STORE_NAME, 'readonly');
    const rows = (await requestToPromise(
      transaction
        .objectStore(STUDENT_STORE_NAME)
        .index(STUDENT_SCOPE_INDEX_NAME)
        .getAll(IDBKeyRange.only(this.buildScopeKey(scope))),
    )) as StudentCacheRowT[];

    try {
      const students = await mapInBatches(rows, async (row) => {
        if (!this.isValidStudentRow(row, scope)) throw new Error('Invalid student cache row');

        const json = await this.decryptText(
          row.encJson,
          dek,
          this.buildStudentAdditionalData(scope, row.studentId),
        );
        const student = JSON.parse(json) as StoredStudentPayloadT;
        handleDates(student);

        if (!student || student.id !== row.studentId || typeof student.name !== 'string') {
          throw new Error('Student cache row payload does not match its key');
        }

        return 'status' in student
          ? (student as StudentT)
          : ({ ...student, status: 'deleted' } as DeletedStudentTombstoneT);
      });

      return students.sort((a, b) => a.id - b.id);
    } catch {
      // [비정상 상황] 저장소 손상, 수동 변경, 과거 버그로 암호문/IV/AAD가 달라지면 인증이 실패한다.
      // 한 row라도 신뢰할 수 없으면 부분 결과를 반환하지 않고 현재 scope 전체를 폐기한다.
      await this.clearScope(scope, scopeLock);
      return null;
    }
  };

  // 학생 payload를 정규화하고 각각 새 IV로 암호화한 IndexedDB row를 만든다.
  private encryptStudentRows = async (
    scope: StudentListCacheScopeT,
    dek: CryptoKey,
    students: readonly StoredStudentPayloadT[],
    updatedAt: string,
  ): Promise<StudentCacheRowT[]> => {
    const uniqueStudentMap = new Map<number, StoredStudentPayloadT>();
    students.forEach((student) => {
      if (!Number.isInteger(student.id) || student.id <= 0 || typeof student.name !== 'string') {
        throw new Error('Invalid student cache payload');
      }

      uniqueStudentMap.set(
        student.id,
        'status' in student && student.status !== 'deleted'
          ? student
          : { id: student.id, name: student.name },
      );
    });

    return mapInBatches([...uniqueStudentMap.values()], async (student) => ({
      ...scope,
      studentId: student.id,
      encJson: await this.encryptText(
        JSON.stringify(student),
        dek,
        this.buildStudentAdditionalData(scope, student.id),
      ),
      updatedAt,
    }));
  };

  // 감싸진 DEK를 복호화해 학생 payload용 non-extractable CryptoKey로 가져온다.
  private decryptAndImportDek = async (
    encDEK: string,
    kek: CryptoKey,
    additionalData: Uint8Array,
  ): Promise<CryptoKey> => {
    const rawDek = await this.decryptBytes(encDEK, kek, additionalData);
    try {
      if (rawDek.byteLength !== AES_KEY_BYTE_LENGTH) throw new Error('Invalid DEK length');
      return await this.importAesKey(rawDek);
    } finally {
      rawDek.fill(0);
    }
  };

  // 서버의 표준 base64 KEK를 검증하고 non-extractable CryptoKey로 가져온다.
  private importKek = async (base64: string): Promise<CryptoKey> => {
    const rawKey = this.decodeBase64(base64);
    try {
      if (rawKey.byteLength !== AES_KEY_BYTE_LENGTH) throw new Error('Invalid KEK length');
      return await this.importAesKey(rawKey);
    } finally {
      rawKey.fill(0);
    }
  };

  // 32-byte AES key를 AES-GCM encrypt/decrypt 용도의 CryptoKey로 가져온다.
  private importAesKey = (rawKey: Uint8Array): Promise<CryptoKey> =>
    crypto.subtle.importKey('raw', this.toArrayBuffer(rawKey), { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt',
    ]);

  // UTF-8 문자열을 AES-GCM encrypted blob JSON 한 줄로 만든다.
  private encryptText = (
    value: string,
    key: CryptoKey,
    additionalData: Uint8Array,
  ): Promise<string> => this.encryptBytes(new TextEncoder().encode(value), key, additionalData);

  // AES-GCM encrypted blob을 복호화해 UTF-8 문자열로 돌려준다.
  private decryptText = async (
    value: string,
    key: CryptoKey,
    additionalData: Uint8Array,
  ): Promise<string> =>
    new TextDecoder().decode(await this.decryptBytes(value, key, additionalData));

  // 매 호출마다 새 12-byte IV를 만들어 byte payload를 AES-GCM으로 암호화한다.
  private encryptBytes = async (
    value: Uint8Array,
    key: CryptoKey,
    additionalData: Uint8Array,
  ): Promise<string> => {
    const iv = crypto.getRandomValues(new Uint8Array(AES_GCM_IV_BYTE_LENGTH));
    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: this.toArrayBuffer(iv),
        additionalData: this.toArrayBuffer(additionalData),
        tagLength: 128,
      },
      key,
      this.toArrayBuffer(value),
    );
    const blob: EncryptedBlobT = {
      alg: 'A256GCM',
      iv: this.encodeBase64(iv),
      ct: this.encodeBase64(new Uint8Array(encrypted)),
    };
    return JSON.stringify(blob);
  };

  // encrypted blob을 검증하고 AAD를 포함해 AES-GCM payload를 복호화한다.
  private decryptBytes = async (
    value: string,
    key: CryptoKey,
    additionalData: Uint8Array,
  ): Promise<Uint8Array> => {
    const blob = this.parseEncryptedBlob(value);
    const iv = this.decodeBase64(blob.iv);
    if (iv.byteLength !== AES_GCM_IV_BYTE_LENGTH) throw new Error('Invalid AES-GCM IV length');

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: this.toArrayBuffer(iv),
        additionalData: this.toArrayBuffer(additionalData),
        tagLength: 128,
      },
      key,
      this.toArrayBuffer(this.decodeBase64(blob.ct)),
    );
    return new Uint8Array(decrypted);
  };

  // 로컬에 저장된 encrypted blob JSON의 알고리즘과 필수 필드를 검증한다.
  private parseEncryptedBlob = (value: string): EncryptedBlobT => {
    const blob = JSON.parse(value) as Partial<EncryptedBlobT>;
    if (blob.alg !== 'A256GCM' || typeof blob.iv !== 'string' || typeof blob.ct !== 'string') {
      throw new Error('Invalid encrypted blob');
    }
    return blob as EncryptedBlobT;
  };

  // Uint8Array를 패딩이 포함된 표준 base64 문자열로 인코딩한다.
  private encodeBase64 = (value: Uint8Array): string => {
    let binary = '';
    for (let index = 0; index < value.byteLength; index += 1) {
      binary += String.fromCharCode(value[index]);
    }
    return btoa(binary);
  };

  // 표준 base64 문자열을 검증하고 Uint8Array로 디코딩한다.
  private decodeBase64 = (value: string): Uint8Array => {
    if (!value || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
      throw new Error('Invalid base64 value');
    }

    const binary = atob(value);
    const result = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      result[index] = binary.charCodeAt(index);
    }
    return result;
  };

  // Web Crypto가 요구하는 독립 ArrayBuffer로 byte view를 복사한다.
  private toArrayBuffer = (value: Uint8Array): ArrayBuffer => {
    const result = new Uint8Array(value.byteLength);
    result.set(value);
    return result.buffer;
  };

  // 요청 scope와 일치하는 DEK가 메모리에 준비됐는지 확인한다.
  private getUnlockedDek = (scope: StudentListCacheScopeT): CryptoKey => {
    this.validateScope(scope);
    if (this.unlockedScope?.cacheKey !== this.buildScopeCacheKey(scope)) {
      throw new Error('Student list cache scope is not unlocked');
    }
    return this.unlockedScope.dek;
  };

  // IndexedDB compound key에 사용할 academy/teacher 식별자를 검증한다.
  private validateScope = (scope: StudentListCacheScopeT): void => {
    if (
      !Number.isInteger(scope.academyId) ||
      scope.academyId <= 0 ||
      !Number.isInteger(scope.teacherId) ||
      scope.teacherId <= 0
    ) {
      throw new Error('Invalid student list cache scope');
    }
  };

  // API가 Date로 변환했을 수 있는 시각을 IndexedDB용 ISO 문자열로 정규화한다.
  private normalizeTimestamp = (value: string | Date, fieldName: string): string => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) throw new Error(`Invalid ${fieldName}`);
    return date.toISOString();
  };

  // metadata가 현재 scope와 cache schema 계약에 맞는지 확인한다.
  private isValidMetadata = (
    metadata: StudentListCacheScopeMetadataT,
    scope: StudentListCacheScopeT,
  ): boolean =>
    metadata.academyId === scope.academyId &&
    metadata.teacherId === scope.teacherId &&
    metadata.schemaVersion === CACHE_SCHEMA_VERSION &&
    typeof metadata.encDEK === 'string' &&
    typeof metadata.lastSyncedAt === 'string' &&
    !Number.isNaN(Date.parse(metadata.lastSyncedAt)) &&
    typeof metadata.lastFullSyncedAt === 'string' &&
    !Number.isNaN(Date.parse(metadata.lastFullSyncedAt));

  // 학생 row의 compound scope, ID, 암호문, 갱신 시각을 검증한다.
  private isValidStudentRow = (row: StudentCacheRowT, scope: StudentListCacheScopeT): boolean =>
    row.academyId === scope.academyId &&
    row.teacherId === scope.teacherId &&
    Number.isInteger(row.studentId) &&
    row.studentId > 0 &&
    typeof row.encJson === 'string' &&
    typeof row.updatedAt === 'string' &&
    !Number.isNaN(Date.parse(row.updatedAt));

  // IndexedDB에서 쓰는 academy/teacher compound key를 만든다.
  private buildScopeKey = (scope: StudentListCacheScopeT): [number, number] => [
    scope.academyId,
    scope.teacherId,
  ];

  // 메모리 DEK가 속한 scope를 비교하기 위한 문자열 key를 만든다.
  private buildScopeCacheKey = (scope: StudentListCacheScopeT): string =>
    `${scope.academyId}:${scope.teacherId}`;

  // DEK 암호문을 다른 scope로 옮겨 쓸 수 없도록 scope AAD를 만든다.
  private buildDekAdditionalData = (scope: StudentListCacheScopeT): Uint8Array =>
    new TextEncoder().encode(
      `student-list-dek:${CACHE_SCHEMA_VERSION}:${scope.academyId}:${scope.teacherId}`,
    );

  // 학생 암호문을 다른 scope나 학생 ID row로 옮겨 쓸 수 없도록 row AAD를 만든다.
  private buildStudentAdditionalData = (
    scope: StudentListCacheScopeT,
    studentId: number,
  ): Uint8Array =>
    new TextEncoder().encode(
      `student-list-row:${CACHE_SCHEMA_VERSION}:${scope.academyId}:${scope.teacherId}:${studentId}`,
    );
}
